import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "golf-trips-page" }
const _hoisted_2 = { class: "top-tabs" }
const _hoisted_3 = ["onClick"]
const _hoisted_4 = { class: "trips-card" }
const _hoisted_5 = { class: "trips-header" }
const _hoisted_6 = { class: "trips-body" }
const _hoisted_7 = {
  key: 0,
  class: "empty-state"
}
const _hoisted_8 = { class: "col renter-col" }
const _hoisted_9 = { class: "renter-info" }
const _hoisted_10 = { class: "renter-name" }
const _hoisted_11 = { class: "renter-phone" }
const _hoisted_12 = { class: "col vehicle-col" }
const _hoisted_13 = { class: "vehicle-name" }
const _hoisted_14 = { class: "vehicle-seats" }
const _hoisted_15 = { class: "col model-id-col" }
const _hoisted_16 = { class: "model-id-badge" }
const _hoisted_17 = { class: "col booking-col" }
const _hoisted_18 = { class: "booking-date" }
const _hoisted_19 = { class: "col financial-col" }
const _hoisted_20 = { class: "payment" }
const _hoisted_21 = { class: "col status-col" }
const _hoisted_22 = { class: "col actions-col" }
const _hoisted_23 = ["onClick"]
const _hoisted_24 = { class: "trips-footer" }
const _hoisted_25 = { class: "results-text" }

import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'

import CommonError from '@/components/CommonError.vue'
import { routeNames } from '@/router/route-names'
import { useGolfTripsStore } from '@/store/golf-trips'
import { useGolfVehiclesStore } from '@/store/golf-vehicles'
import { useGolfHeaderStore } from '@/store/golf-header'
import { useGolfI18n } from '@/composables/useGolfI18n'
import type { GolfTrip, GolfCarProfile } from '@/api/golf'

const perPage = 12


export default /*@__PURE__*/_defineComponent({
  __name: 'List',
  setup(__props) {

const router = useRouter()
const golfStore = useGolfTripsStore()
const golfVehiclesStore = useGolfVehiclesStore()
const headerStore = useGolfHeaderStore()
const { t: golfT } = useGolfI18n()

const tabs = computed(() => [
  { label: golfT('golf.trips.tab_all'), value: 'all' },
  { label: golfT('golf.trips.tab_pending'), value: 'pending' },
  { label: golfT('golf.trips.tab_approved'), value: 'approved' },
  { label: golfT('golf.trips.tab_ongoing'), value: 'ongoing' },
  { label: golfT('golf.trips.tab_on_hold'), value: 'on_hold' },
  { label: golfT('golf.trips.tab_completed'), value: 'completed' },
  { label: golfT('golf.trips.tab_cancelled'), value: 'cancelled' },
])

const currentTab = ref('all')
const currentPage = ref(1)
const searchQuery = computed({
  get: () => headerStore.searchQuery,
  set: (val) => headerStore.setSearchQuery(val)
})
const setTab = (value: string) => {
  currentTab.value = value
  currentPage.value = 1
}

watch(searchQuery, () => {
  currentPage.value = 1
})

const profileMap = computed(() => {
  const map: Record<string, GolfCarProfile> = {}
  for (const p of golfVehiclesStore.profiles) {
    map[p.id] = p
  }
  return map
})

onMounted(() => {
  headerStore.setHeaderInfo('golf.trips.title', true, 'golf.header.search_placeholder')
  golfStore.fetchTrips()
  golfVehiclesStore.fetchProfiles()
})

const getRenter = (trip: GolfTrip) => {
  return trip.renter
}

const getCar = (trip: GolfTrip) => {
  return trip.golf_car_profile || trip.profile || profileMap.value[trip.golf_car_profile_id]
}

const formatRenterName = (trip: GolfTrip) => {
  const user = getRenter(trip)
  if (user?.first_name && user?.last_name) {
    return `${user.first_name} ${user.last_name[0]}.`
  }
  return user?.name || trip.user_id
}

const formatRenterPhone = (trip: GolfTrip) => {
  const user = getRenter(trip)
  const phone = user?.phone || ''
  if (!phone) return '—'
  const digits = phone.replace(/\D/g, '')
  const last2 = digits.slice(-2)
  const prefix = phone.startsWith('+')
    ? phone.slice(0, Math.min(4, phone.length - 2))
    : `+${digits.slice(0, 3)}`
  return `${prefix} ***-***-${last2}`
}

const getCarName = (trip: GolfTrip) => {
  const car = getCar(trip)
  return car?.name || car?.model_name || '—'
}

const getCarSeats = (trip: GolfTrip) => {
  const car = getCar(trip)
  return car?.seats || 4
}

const getModelId = (trip: GolfTrip) => {
  return trip.asset?.asset_number || (trip.asset?.id ? '#' + trip.asset.id.slice(0, 8) : '—')
}

const formatStatus = (status: string) => {
  const map: Record<string, string> = {
    pending: golfT('golf.trips.tab_pending'),
    approved: golfT('golf.trips.tab_approved'),
    ongoing: golfT('golf.trips.tab_ongoing'),
    on_hold: golfT('golf.trips.tab_on_hold'),
    completed: golfT('golf.trips.tab_completed'),
    cancelled_by_user: golfT('golf.trips.status_cancelled_user'),
    cancelled_by_admin: golfT('golf.trips.status_cancelled_admin'),
    cancelled_by_system: `${golfT('golf.trips.tab_cancelled')} (${golfT('golf.common.system')})`,
  }
  return map[status] || status
}

const getStatusClass = (status: string) => {
  if (status.startsWith('cancelled')) return 'status-cancelled'
  return `status-${status}`
}

const getTypeClass = (type?: string) => {
  if (type === 'hourly') return 'type-hourly'
  if (type === 'daily') return 'type-daily'
  return 'type-other'
}

const formatBookingDate = (dateStr?: string) => {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  const date = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
  const time = d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  return `${date}, ${time}`
}

const viewTrip = (trip: GolfTrip) => {
  router.push({ name: routeNames.golfTripDetails, params: { id: trip.id } })
}

const matchesStatus = (status: string) => {
  if (currentTab.value === 'all') return true
  if (currentTab.value === 'cancelled') return status.startsWith('cancelled')
  return status === currentTab.value
}

const filteredTrips = computed(() => {
  let list = Array.isArray(golfStore.trips) ? golfStore.trips : []
  const query = searchQuery.value.trim().toLowerCase()

  list = list.filter((trip: GolfTrip) => matchesStatus(trip.status))

  if (query) {
    list = list.filter((trip: GolfTrip) => {
      const renter = getRenter(trip)
      const car = getCar(trip)
      const modelId = getModelId(trip)
      return (
        trip.id.toLowerCase().includes(query) ||
        modelId.toLowerCase().includes(query) ||
        (renter?.first_name && renter.first_name.toLowerCase().includes(query)) ||
        (renter?.last_name && renter.last_name.toLowerCase().includes(query)) ||
        (renter?.name && renter.name.toLowerCase().includes(query)) ||
        (renter?.phone && renter.phone.toLowerCase().includes(query)) ||
        (car?.name && car.name.toLowerCase().includes(query)) ||
        (car?.model_name && car.model_name.toLowerCase().includes(query))
      )
    })
  }

  return list
})

const showingStart = computed(() => {
  return filteredTrips.value.length === 0
    ? 0
    : (currentPage.value - 1) * perPage + 1
})

const showingEnd = computed(() => {
  return Math.min(currentPage.value * perPage, filteredTrips.value.length)
})

const paginatedTrips = computed(() => {
  const start = (currentPage.value - 1) * perPage
  return filteredTrips.value.slice(start, start + perPage)
})

const onRetryPress = () => {
  golfStore.fetchTrips()
  golfVehiclesStore.fetchProfiles()
}

return (_ctx: any,_cache: any) => {
  const _component_el_avatar = _resolveComponent("el-avatar")!
  const _component_el_pagination = _resolveComponent("el-pagination")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_unref(golfStore).error)
      ? (_openBlock(), _createBlock(CommonError, {
          key: 0,
          errorMessage: _unref(golfStore).error,
          onRetryPress: onRetryPress
        }, null, 8, ["errorMessage"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tabs.value, (tab) => {
          return (_openBlock(), _createElementBlock("button", {
            key: tab.value,
            class: _normalizeClass(['tab-btn', { active: currentTab.value === tab.value }]),
            onClick: ($event: any) => (setTab(tab.value))
          }, _toDisplayString(tab.label), 11, _hoisted_3))
        }), 128))
      ]),
      _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("span", null, _toDisplayString(_ctx.$t('golf.trips.renter_profile')), 1),
          _createElementVNode("span", null, _toDisplayString(_ctx.$t('golf.trips.vehicle_model')), 1),
          _createElementVNode("span", null, _toDisplayString(_ctx.$t('golf.trips.model_id')), 1),
          _createElementVNode("span", null, _toDisplayString(_ctx.$t('golf.trips.booking_details')), 1),
          _createElementVNode("span", null, _toDisplayString(_ctx.$t('golf.trips.financials')), 1),
          _createElementVNode("span", null, _toDisplayString(_ctx.$t('golf.trips.lifecycle_status')), 1),
          _createElementVNode("span", null, _toDisplayString(_ctx.$t('golf.trips.actions')), 1)
        ]),
        _createElementVNode("div", _hoisted_6, [
          (paginatedTrips.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(_ctx.$t('golf.trips.no_trips_found')), 1))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(paginatedTrips.value, (trip) => {
            return (_openBlock(), _createElementBlock("div", {
              key: trip.id,
              class: "trip-row"
            }, [
              _createElementVNode("div", _hoisted_8, [
                _createVNode(_component_el_avatar, {
                  size: 44,
                  src: getRenter(trip)?.image || undefined,
                  class: "renter-avatar"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(formatRenterName(trip).charAt(0)), 1)
                  ]),
                  _: 2
                }, 1032, ["src"]),
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("div", _hoisted_10, _toDisplayString(formatRenterName(trip)), 1),
                  _createElementVNode("div", _hoisted_11, _toDisplayString(formatRenterPhone(trip)), 1)
                ])
              ]),
              _createElementVNode("div", _hoisted_12, [
                _createElementVNode("div", _hoisted_13, _toDisplayString(getCarName(trip)), 1),
                _createElementVNode("div", _hoisted_14, _toDisplayString(getCarSeats(trip)) + " " + _toDisplayString(_ctx.$t('golf.trips.seats')), 1)
              ]),
              _createElementVNode("div", _hoisted_15, [
                _createElementVNode("span", _hoisted_16, _toDisplayString(getModelId(trip)), 1)
              ]),
              _createElementVNode("div", _hoisted_17, [
                _createElementVNode("span", {
                  class: _normalizeClass(["booking-type", getTypeClass(trip.pricing_type)])
                }, _toDisplayString((trip.pricing_type || '—').toUpperCase()), 3),
                _createElementVNode("span", _hoisted_18, _toDisplayString(formatBookingDate(trip.created_at)), 1)
              ]),
              _createElementVNode("div", _hoisted_19, [
                _createElementVNode("div", {
                  class: _normalizeClass(["amount", {
                'amount-dim': !trip.total_amount || trip.total_amount === 0,
              }])
                }, " ₼" + _toDisplayString((trip.total_amount || 0).toFixed(2)), 3),
                _createElementVNode("div", _hoisted_20, _toDisplayString((trip.payment_method || '—').toUpperCase()), 1)
              ]),
              _createElementVNode("div", _hoisted_21, [
                _createElementVNode("span", {
                  class: _normalizeClass(["status-badge", getStatusClass(trip.status)])
                }, _toDisplayString(formatStatus(trip.status)), 3)
              ]),
              _createElementVNode("div", _hoisted_22, [
                _createElementVNode("span", {
                  class: "view-details",
                  onClick: ($event: any) => (viewTrip(trip))
                }, _toDisplayString(_ctx.$t('golf.trips.view_details')), 9, _hoisted_23)
              ])
            ]))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_24, [
          _createElementVNode("span", _hoisted_25, _toDisplayString(_ctx.$t('golf.trips.showing_results', {
              start: showingStart.value,
              end: showingEnd.value,
              total: filteredTrips.value.length,
            })), 1),
          _createVNode(_component_el_pagination, {
            "current-page": currentPage.value,
            "onUpdate:currentPage": _cache[0] || (_cache[0] = ($event: any) => ((currentPage).value = $event)),
            "page-size": perPage,
            total: filteredTrips.value.length,
            layout: "prev, pager, next"
          }, null, 8, ["current-page", "total"])
        ])
      ])), [
        [_directive_loading, _unref(golfStore).isLoading]
      ])
    ])
  ], 64))
}
}

})